import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-IN") : "—");
const STATUS_BADGE = { Outstanding: "bg-amber-500", "Partially Paid": "bg-blue-500", Paid: "bg-emerald-600" };

function Item({ label, value, testId }) {
  return (
    <div>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-sm font-medium text-slate-900" data-testid={testId}>{value ?? "—"}</div>
    </div>
  );
}

export default function AccommodationDetailDialog({ roomId, initial, canManage, onClose, onChanged, onEdit }) {
  const [room, setRoom] = useState(initial || null);
  const [confirmVacate, setConfirmVacate] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () =>
    client.get(`/accommodation/rooms/${roomId}`).then((res) => setRoom(res.data)).catch((err) => toast.error(getErrorMessage(err)));

  useEffect(() => { load(); }, [roomId]);

  const changeStatus = async (action) => {
    setBusy(true);
    try {
      await client.post(`/accommodation/rooms/${roomId}/${action}`);
      toast.success(action === "vacate" ? "Marked as Vacated" : "Accommodation reactivated");
      setConfirmVacate(false);
      await load();
      onChanged();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="accommodation-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {roomId}
            {room && <Badge className={room.status === "Active" ? "bg-emerald-600" : "bg-slate-400"}>{room.status}</Badge>}
          </DialogTitle>
        </DialogHeader>
        {!room ? (
          <p className="text-slate-400 py-6 text-center">Loading...</p>
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-3 gap-4">
              <Item label="Landlord" value={`${room.vendor_name} (${room.vendor_code})`} testId="accommodation-detail-landlord" />
              <Item label="Site" value={room.site_label} testId="accommodation-detail-site" />
              <Item label="Monthly Rent" value={money(room.monthly_rent)} testId="accommodation-detail-rent" />
              <Item label="Rent Start Date" value={fmt(room.rent_start_date)} />
              <Item label="Cycle Start Day / Rent Due Day" value={`${room.cycle_start_day ?? "—"} / ${room.rent_due_day ?? "—"}`} />
              <Item label="Deposit" value={room.deposit_amount != null ? `${money(room.deposit_amount)} — ${room.deposit_status}` : room.deposit_status} testId="accommodation-detail-deposit" />
              <Item label="Deposit Paid Date" value={fmt(room.deposit_paid_date)} />
              <Item label="Lease" value={`${fmt(room.lease_start_date)} to ${fmt(room.lease_end_date)}`} />
            </div>
            {room.remarks && <p className="text-sm text-slate-600">{room.remarks}</p>}

            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-slate-800">Vendor Bills for this room</h3>
                <span className="text-xs text-slate-500" data-testid="accommodation-detail-totals">
                  {room.bills ? `Billed ${money(room.total_billed)} · Outstanding ${money(room.total_outstanding)}` : "Loading…"}
                </span>
              </div>
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <Table data-testid="accommodation-detail-bills-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Bill ID</TableHead>
                      <TableHead>Period</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Outstanding</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!room.bills ? (
                      <TableRow><TableCell colSpan={5} className="text-center py-6 text-slate-400" data-testid="accommodation-detail-bills-loading">Loading bills…</TableCell></TableRow>
                    ) : room.bills.length === 0 ? (
                      <TableRow><TableCell colSpan={5} className="text-center py-6 text-slate-400">No bills yet.</TableCell></TableRow>
                    ) : (
                      room.bills.map((b) => (
                        <TableRow key={b.bill_id} data-testid={`accommodation-detail-bill-${b.bill_id}`}>
                          <TableCell className="font-mono text-xs font-semibold">{b.bill_id}</TableCell>
                          <TableCell className="text-xs">{b.bill_from ? `${fmt(b.bill_from)} – ${fmt(b.bill_to)}` : fmt(b.bill_date)}</TableCell>
                          <TableCell>{money(b.amount)}</TableCell>
                          <TableCell className="font-medium">{money(b.outstanding_balance)}</TableCell>
                          <TableCell><Badge className={STATUS_BADGE[b.status]}>{b.status}</Badge></TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            {confirmVacate && (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="accommodation-vacate-warning">
                {room.unpaid_bill_count > 0
                  ? `Warning: this room still has ${room.unpaid_bill_count} unpaid bill(s). They stay payable after marking it Vacated.`
                  : "Mark this room as Vacated? Its bill history will stay."}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose} data-testid="accommodation-detail-close-button">Close</Button>
              {canManage && (
                <>
                  <Button variant="outline" onClick={() => onEdit(room)} data-testid="accommodation-detail-edit-button">Edit</Button>
                  {room.status === "Active" ? (
                    confirmVacate ? (
                      <Button disabled={busy} variant="destructive" onClick={() => changeStatus("vacate")} data-testid="accommodation-vacate-confirm-button">Confirm Vacated</Button>
                    ) : (
                      <Button variant="destructive" disabled={!room.bills} onClick={() => setConfirmVacate(true)} data-testid="accommodation-vacate-button">Mark Vacated</Button>
                    )
                  ) : (
                    <Button disabled={busy} onClick={() => changeStatus("reactivate")} className="bg-amber-600 hover:bg-amber-700" data-testid="accommodation-reactivate-button">Reactivate</Button>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
